import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { PrintableSubcontractDocument } from '@/components/epc/PrintableSubcontractAgreement';
import { ConvertCertificationToSiModal } from '@/components/epc/ConvertCertificationToSiModal';
import { ENTITY_TYPE_SUBCONTRACT_PAYMENT_CERTIFICATE } from '@/constants/approval-entity-types';
import { epcSubcontractAdminService } from '@/services/epcSubcontractAdmin.service';
import { formatMoney } from '@/utils/format';
import { openPmvPrintOverlay } from '@/utils/openPmvPrintOverlay';
import { previewSpcValuation } from '@/utils/spcValuationPreview';

const { Title, Text } = Typography;

type SpcLineDraft = {
  key: string;
  agreementLineId: string;
  lineCode: string;
  description: string;
  agreementQty: number | null;
  unitRate: number | null;
  lineAmount: number;
  previousCertified: number;
  remaining: number;
  thisPeriodQty: number;
  thisPeriodPct: number;
  appliedAmount: number;
};

function apiErr(e: unknown): string {
  const ax = e as {
    response?: { data?: { message?: string; errors?: { message?: string } } };
    message?: string;
  };
  const data = ax?.response?.data;
  return data?.message || data?.errors?.message || ax?.message || 'Request failed';
}

function ymd(d: Dayjs | null | undefined): string | undefined {
  if (!d) return undefined;
  return d.format('YYYY-MM-DD');
}

function round2(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function money(v: unknown, currency?: string) {
  return formatMoney(v, { currency: currency || 'AED' });
}

const EpcSubcontractPaymentCertificateFormPage: React.FC = () => {
  const { id: agreementId, spcId } = useParams<{ id: string; spcId?: string }>();
  const isNew = !spcId || spcId === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const applyCepOnLoad = searchParams.get('cep') === '1';
  const [form] = Form.useForm();
  const [lines, setLines] = useState<SpcLineDraft[]>([]);
  const [useAdvanceRate, setUseAdvanceRate] = useState(true);
  const [useRetentionRate, setUseRetentionRate] = useState(true);
  const [convertSiOpen, setConvertSiOpen] = useState(false);

  const kindWatch = Form.useWatch('certificateKind', form) as string | undefined;
  const mosWatch = Form.useWatch('materialsOnSite', form) as number | undefined;
  const dayworksWatch = Form.useWatch('dayworksAmount', form) as number | undefined;
  const attendanceWatch = Form.useWatch('attendancePct', form) as number | undefined;
  const freeIssueWatch = Form.useWatch('freeIssueAmount', form) as number | undefined;
  const includeMosWatch = Form.useWatch('includeMaterialsOnSite', form) as boolean | undefined;
  const advRateWatch = Form.useWatch('advanceRecoveryRatePct', form) as number | undefined;
  const advFixedWatch = Form.useWatch('advanceRecoveryAmount', form) as number | undefined;
  const remAdvWatch = Form.useWatch('remainingAdvanceBalance', form) as number | undefined;
  const retRateWatch = Form.useWatch('retentionRatePct', form) as number | undefined;
  const retFixedWatch = Form.useWatch('retentionWithheldAmount', form) as number | undefined;
  const retRelWatch = Form.useWatch('retentionReleasedAmount', form) as number | undefined;
  const setOffWatch = Form.useWatch('setOffAmount', form) as number | undefined;

  const draftQ = useQuery({
    queryKey: ['epc-spc-draft', agreementId, spcId],
    queryFn: () =>
      epcSubcontractAdminService.getPaymentCertificateDraft(
        agreementId!,
        isNew ? undefined : spcId
      ),
    enabled: Boolean(agreementId),
  });

  const spcQ = useQuery({
    queryKey: ['epc-spc', spcId],
    queryFn: () => epcSubcontractAdminService.getPaymentCertificate(spcId!),
    enabled: Boolean(spcId) && !isNew,
  });

  const certsQ = useQuery({
    queryKey: ['epc-subcontract-certs-for-spc', agreementId],
    queryFn: () => epcSubcontractAdminService.listCertifications(agreementId!),
    enabled: Boolean(agreementId) && !isNew,
  });

  const agreement = draftQ.data?.agreement;
  const defaults = draftQ.data?.defaults;
  const currency = String(
    spcQ.data?.currency || defaults?.currency || agreement?.currency || 'AED'
  );
  const status = String(spcQ.data?.status || 'Draft');
  const canEdit = isNew || status === 'Draft' || status === 'Rejected';
  const agreementStatus = String(agreement?.status || '');
  const agreementActive =
    agreementStatus === 'Active' || agreementStatus === 'Approved';

  const workGross = useMemo(
    () => round2(lines.reduce((s, ln) => s + (Number(ln.appliedAmount) || 0), 0)),
    [lines]
  );

  const preview = useMemo(
    () =>
      previewSpcValuation({
        workGross,
        materialsOnSite: mosWatch != null ? Number(mosWatch) : 0,
        dayworksAmount: dayworksWatch != null ? Number(dayworksWatch) : 0,
        attendancePct: attendanceWatch != null ? Number(attendanceWatch) : 0,
        includeMaterialsOnSite: includeMosWatch !== false,
        freeIssueAmount: freeIssueWatch != null ? Number(freeIssueWatch) : 0,
        advanceRecoveryRatePct: useAdvanceRate
          ? advRateWatch != null
            ? Number(advRateWatch)
            : undefined
          : undefined,
        advanceRecoveryAmount: !useAdvanceRate
          ? advFixedWatch != null
            ? Number(advFixedWatch)
            : 0
          : undefined,
        remainingAdvanceBalance: remAdvWatch != null ? Number(remAdvWatch) : undefined,
        retentionRatePct: useRetentionRate
          ? retRateWatch != null
            ? Number(retRateWatch)
            : undefined
          : undefined,
        retentionWithheldAmount: !useRetentionRate
          ? retFixedWatch != null
            ? Number(retFixedWatch)
            : 0
          : undefined,
        retentionReleasedAmount: retRelWatch != null ? Number(retRelWatch) : 0,
        setOffAmount: setOffWatch != null ? Number(setOffWatch) : 0,
      }),
    [
      workGross,
      mosWatch,
      dayworksWatch,
      attendanceWatch,
      includeMosWatch,
      freeIssueWatch,
      useAdvanceRate,
      advRateWatch,
      advFixedWatch,
      remAdvWatch,
      useRetentionRate,
      retRateWatch,
      retFixedWatch,
      retRelWatch,
      setOffWatch,
    ]
  );

  const linkedCert = useMemo(() => {
    if (!spcId || isNew) return null;
    const list = certsQ.data || [];
    return (
      list.find(
        (c) => String(c.payment_certificate_id || c.paymentCertificateId) === String(spcId)
      ) || null
    );
  }, [certsQ.data, spcId, isNew]);

  // Seed lines + form for new SPC
  useEffect(() => {
    if (!isNew || !draftQ.data) return;
    const seeded: SpcLineDraft[] = (draftQ.data.lines || []).map((ln, i) => ({
      key: String(ln.agreementLineId || `new-${i}`),
      agreementLineId: String(ln.agreementLineId),
      lineCode: String(ln.lineCode || ''),
      description: String(ln.description || ''),
      agreementQty: ln.qty != null ? Number(ln.qty) : null,
      unitRate: ln.unitRate != null ? Number(ln.unitRate) : null,
      lineAmount: Number(ln.lineAmount) || 0,
      previousCertified: Number(ln.previousCertifiedAmount) || 0,
      remaining: Number(ln.remainingAmount) || 0,
      thisPeriodQty: 0,
      thisPeriodPct: 0,
      appliedAmount: 0,
    }));
    setLines(
      seeded.length
        ? seeded
        : [
            {
              key: 'fallback',
              agreementLineId: '',
              lineCode: '',
              description: String(agreement?.title || 'Contract sum'),
              agreementQty: 1,
              unitRate: Number(agreement?.contractSum) || 0,
              lineAmount: Number(agreement?.contractSum) || 0,
              previousCertified: 0,
              remaining: Number(agreement?.contractSum) || 0,
              thisPeriodQty: 0,
              thisPeriodPct: 0,
              appliedAmount: 0,
            },
          ]
    );
    form.setFieldsValue({
      certificateKind: 'Interim',
      attendancePct: defaults?.attendancePct ?? undefined,
      advanceRecoveryRatePct: defaults?.advanceRecoveryRatePct ?? undefined,
      retentionRatePct: defaults?.retentionRatePct ?? undefined,
      remainingAdvanceBalance: defaults?.remainingAdvanceBalance ?? undefined,
      includeMaterialsOnSite: true,
      materialsOnSite: 0,
      dayworksAmount: 0,
      freeIssueAmount: 0,
      retentionReleasedAmount: 0,
      setOffAmount: 0,
    });
    setUseAdvanceRate(defaults?.advanceRecoveryRatePct != null);
    setUseRetentionRate(defaults?.retentionRatePct != null);
  }, [isNew, draftQ.data, form, agreement, defaults]);

  // Load existing SPC
  useEffect(() => {
    if (isNew || !spcQ.data || !draftQ.data) return;
    const spc = spcQ.data;
    const draftLines = draftQ.data.lines || [];
    const byAgr = new Map(draftLines.map((l) => [String(l.agreementLineId), l]));
    const spcLines = Array.isArray(spc.lines) ? (spc.lines as Record<string, unknown>[]) : [];

    if (spcLines.length) {
      setLines(
        spcLines.map((ln, i) => {
          const agrId = String(ln.agreement_line_id || ln.agreementLineId || '');
          const seed = byAgr.get(agrId);
          const prev =
            ln.previous_certified_amount != null
              ? Number(ln.previous_certified_amount)
              : Number(seed?.previousCertifiedAmount) || 0;
          const lineAmount = Number(seed?.lineAmount) || 0;
          const applied = Number(ln.applied_amount ?? ln.appliedAmount) || 0;
          const meta =
            typeof ln.meta_json === 'string'
              ? (() => {
                  try {
                    return JSON.parse(ln.meta_json as string) as Record<string, unknown>;
                  } catch {
                    return {};
                  }
                })()
              : ((ln.meta_json || ln.metaJson || {}) as Record<string, unknown>);
          return {
            key: String(ln.id || `spc-${i}`),
            agreementLineId: agrId,
            lineCode: String(ln.line_code || ln.lineCode || seed?.lineCode || ''),
            description: String(ln.description || seed?.description || ''),
            agreementQty: seed?.qty != null ? Number(seed.qty) : null,
            unitRate:
              ln.unit_rate != null
                ? Number(ln.unit_rate)
                : seed?.unitRate != null
                  ? Number(seed.unitRate)
                  : null,
            lineAmount,
            previousCertified: prev,
            remaining: Math.max(0, round2(lineAmount - prev)),
            thisPeriodQty: Number(meta.thisPeriodQty ?? ln.quantity ?? 0) || 0,
            thisPeriodPct: Number(meta.thisPeriodPct ?? 0) || 0,
            appliedAmount: applied,
          };
        })
      );
    } else {
      setLines(
        draftLines.map((ln, i) => ({
          key: String(ln.agreementLineId || `d-${i}`),
          agreementLineId: String(ln.agreementLineId),
          lineCode: String(ln.lineCode || ''),
          description: String(ln.description || ''),
          agreementQty: ln.qty != null ? Number(ln.qty) : null,
          unitRate: ln.unitRate != null ? Number(ln.unitRate) : null,
          lineAmount: Number(ln.lineAmount) || 0,
          previousCertified: Number(ln.previousCertifiedAmount) || 0,
          remaining: Number(ln.remainingAmount) || 0,
          thisPeriodQty: 0,
          thisPeriodPct: 0,
          appliedAmount: 0,
        }))
      );
    }

    const terms =
      typeof spc.commercial_terms_json === 'string'
        ? (() => {
            try {
              return JSON.parse(String(spc.commercial_terms_json)) as Record<string, unknown>;
            } catch {
              return {};
            }
          })()
        : ((spc.commercial_terms_json || {}) as Record<string, unknown>);

    const advRate = terms.advanceRecoveryRatePct ?? defaults?.advanceRecoveryRatePct;
    const retRate = terms.retentionRatePct ?? defaults?.retentionRatePct;
    setUseAdvanceRate(Boolean(advRate) && terms.advanceRecoveryFixed == null);
    setUseRetentionRate(Boolean(retRate) && terms.retentionFixed == null);

    form.setFieldsValue({
      certificateKind: spc.certificate_kind || 'Interim',
      periodFrom: spc.period_from ? dayjs(String(spc.period_from)) : undefined,
      periodTo: spc.period_to ? dayjs(String(spc.period_to)) : undefined,
      materialsOnSite: Number(spc.materials_on_site_amount) || 0,
      dayworksAmount: Number(spc.dayworks_amount) || 0,
      attendancePct:
        terms.attendanceRatePct != null
          ? Number(terms.attendanceRatePct)
          : defaults?.attendancePct ?? undefined,
      freeIssueAmount: Number(spc.free_issue_excluded_amount) || 0,
      includeMaterialsOnSite: true,
      advanceRecoveryRatePct: advRate != null ? Number(advRate) : undefined,
      advanceRecoveryAmount: Number(spc.advance_recovery_amount) || 0,
      remainingAdvanceBalance:
        defaults?.remainingAdvanceBalance != null
          ? Number(defaults.remainingAdvanceBalance)
          : undefined,
      retentionRatePct: retRate != null ? Number(retRate) : undefined,
      retentionWithheldAmount: Number(spc.retention_withheld_amount) || 0,
      retentionReleasedAmount: Number(spc.retention_released_amount) || 0,
      setOffAmount: Number(spc.set_off_amount) || 0,
    });
  }, [isNew, spcQ.data, draftQ.data, form, defaults]);

  // Apply CEP on ?cep=1 (once after lines seeded)
  const cepAppliedRef = React.useRef(false);
  useEffect(() => {
    if (!isNew || !applyCepOnLoad || !agreementId || !lines.length || cepAppliedRef.current) return;
    cepAppliedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const hints = await epcSubcontractAdminService.getCepProgressHints(agreementId);
        if (cancelled) return;
        const hintLines = hints.lines || [];
        if (!hintLines.length) {
          message.info(hints.note || 'No CEP progress hints available');
          return;
        }
        const byId = new Map(
          hintLines.map((h) => [String(h.agreementLineId || h.agreement_line_id), h])
        );
        setLines((prev) =>
          prev.map((ln) => {
            const h = byId.get(ln.agreementLineId);
            if (!h) return ln;
            const pct = Number(h.progressPct) || 0;
            const suggested = Number(h.suggestedAmount);
            const applied =
              Number.isFinite(suggested) && suggested > 0
                ? round2(suggested)
                : round2((ln.lineAmount * pct) / 100);
            const qty =
              ln.agreementQty && ln.agreementQty > 0
                ? round2((ln.agreementQty * pct) / 100)
                : Number(h.suggestedQty) || 0;
            return {
              ...ln,
              thisPeriodPct: pct,
              thisPeriodQty: qty,
              appliedAmount: Math.min(applied, ln.remaining || applied),
            };
          })
        );
        message.success('CEP progress applied to period quantities (advisory)');
      } catch (e: unknown) {
        message.warning(apiErr(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, applyCepOnLoad, agreementId, lines.length]);

  const updateLine = (key: string, patch: Partial<SpcLineDraft>) => {
    setLines((prev) =>
      prev.map((ln) => {
        if (ln.key !== key) return ln;
        const next = { ...ln, ...patch };
        // Recompute applied from qty or % when those change
        if (patch.thisPeriodPct != null && patch.appliedAmount == null && patch.thisPeriodQty == null) {
          const pct = Number(patch.thisPeriodPct) || 0;
          next.appliedAmount = round2(
            Math.min(ln.remaining || ln.lineAmount, (ln.lineAmount * pct) / 100)
          );
          if (ln.agreementQty && ln.agreementQty > 0) {
            next.thisPeriodQty = round2((ln.agreementQty * pct) / 100);
          }
        } else if (
          patch.thisPeriodQty != null &&
          patch.appliedAmount == null &&
          patch.thisPeriodPct == null
        ) {
          const rate = ln.unitRate != null ? Number(ln.unitRate) : 0;
          next.appliedAmount = round2(
            Math.min(ln.remaining || ln.lineAmount, (Number(patch.thisPeriodQty) || 0) * rate)
          );
          if (ln.lineAmount > 0) {
            next.thisPeriodPct = round2((next.appliedAmount / ln.lineAmount) * 100);
          }
        } else if (patch.appliedAmount != null && patch.thisPeriodPct == null) {
          if (ln.lineAmount > 0) {
            next.thisPeriodPct = round2((Number(patch.appliedAmount) / ln.lineAmount) * 100);
          }
        }
        if (next.appliedAmount > next.remaining + 0.009) {
          next.appliedAmount = next.remaining;
        }
        return next;
      })
    );
  };

  const buildPayload = (values: Record<string, unknown>) => {
    const payloadLines = lines.map((ln) => ({
      agreementLineId: ln.agreementLineId || undefined,
      lineCode: ln.lineCode || undefined,
      description: ln.description,
      quantity: ln.thisPeriodQty || undefined,
      unitRate: ln.unitRate ?? undefined,
      appliedAmount: round2(ln.appliedAmount),
      previousCertifiedAmount: round2(ln.previousCertified),
      thisPeriodPct: ln.thisPeriodPct,
      thisPeriodQty: ln.thisPeriodQty,
      metaJson: {
        thisPeriodPct: ln.thisPeriodPct,
        thisPeriodQty: ln.thisPeriodQty,
        lineAmount: ln.lineAmount,
        cumulativeAmount: round2(ln.previousCertified + ln.appliedAmount),
      },
    }));
    const work = round2(payloadLines.reduce((s, l) => s + l.appliedAmount, 0));
    return {
      agreementId,
      certificateKind: values.certificateKind || 'Interim',
      periodFrom: ymd(values.periodFrom as Dayjs | null),
      periodTo: ymd(values.periodTo as Dayjs | null),
      currency,
      grossAppliedAmount: work,
      lines: payloadLines,
      materialsOnSite: values.materialsOnSite != null ? Number(values.materialsOnSite) : 0,
      dayworksAmount: values.dayworksAmount != null ? Number(values.dayworksAmount) : 0,
      attendancePct: values.attendancePct != null ? Number(values.attendancePct) : undefined,
      freeIssueAmount: values.freeIssueAmount != null ? Number(values.freeIssueAmount) : 0,
      includeMaterialsOnSite: values.includeMaterialsOnSite !== false,
      advanceRecoveryRatePct: useAdvanceRate
        ? values.advanceRecoveryRatePct != null
          ? Number(values.advanceRecoveryRatePct)
          : undefined
        : undefined,
      advanceRecoveryAmount: !useAdvanceRate
        ? values.advanceRecoveryAmount != null
          ? Number(values.advanceRecoveryAmount)
          : 0
        : undefined,
      remainingAdvanceBalance:
        values.remainingAdvanceBalance != null
          ? Number(values.remainingAdvanceBalance)
          : undefined,
      retentionRatePct: useRetentionRate
        ? values.retentionRatePct != null
          ? Number(values.retentionRatePct)
          : undefined
        : undefined,
      retentionWithheldAmount: !useRetentionRate
        ? values.retentionWithheldAmount != null
          ? Number(values.retentionWithheldAmount)
          : 0
        : undefined,
      retentionReleasedAmount:
        values.retentionReleasedAmount != null
          ? Number(values.retentionReleasedAmount)
          : 0,
      setOffAmount: values.setOffAmount != null ? Number(values.setOffAmount) : 0,
      cumulativeGrossAmount: round2(
        work +
          lines.reduce((s, ln) => s + (Number(ln.previousCertified) || 0), 0)
      ),
    };
  };

  const siteEvidenceMut = useMutation({
    mutationFn: () => epcSubcontractAdminService.getSiteMaterialEvidence(agreementId!),
    onSuccess: (ev) => {
      const freeAmt = Number(ev.suggested_free_issue_excluded_amount) || 0;
      const mosAmt = Number(ev.suggested_materials_on_site_amount) || 0;
      form.setFieldsValue({
        freeIssueAmount: freeAmt,
        ...(mosAmt > 0 ? { materialsOnSite: mosAmt } : {}),
      });
      message.success(
        ev.note
          ? `Suggested free-issue ${freeAmt}. ${ev.note}`
          : `Suggested free-issue excluded amount ${freeAmt}`
      );
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  const saveM = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isNew) return epcSubcontractAdminService.createPaymentCertificate(payload);
      return epcSubcontractAdminService.patchPaymentCertificate(spcId!, payload);
    },
    onSuccess: (saved) => {
      message.success(isNew ? 'Payment certificate created' : 'Payment certificate saved');
      qc.invalidateQueries({ queryKey: ['epc-subcontract-workspace', agreementId] });
      const newId = String(saved?.id || spcId);
      navigate(`/epc/commercial/subcontracts/${agreementId}/certificates/${newId}`, {
        replace: true,
      });
      qc.invalidateQueries({ queryKey: ['epc-spc', newId] });
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  const submitM = useMutation({
    mutationFn: () => epcSubcontractAdminService.submitPaymentCertificate(spcId!),
    onSuccess: () => {
      message.success('SPC submitted for approval');
      qc.invalidateQueries({ queryKey: ['epc-spc', spcId] });
      qc.invalidateQueries({ queryKey: ['epc-subcontract-workspace', agreementId] });
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  const createCertM = useMutation({
    mutationFn: () =>
      epcSubcontractAdminService.createCertification({ paymentCertificateId: spcId }),
    onSuccess: () => {
      message.success('Certification created');
      qc.invalidateQueries({ queryKey: ['epc-subcontract-certs-for-spc', agreementId] });
      qc.invalidateQueries({ queryKey: ['epc-subcontract-workspace', agreementId] });
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  const confirmCertM = useMutation({
    mutationFn: (certId: string) => epcSubcontractAdminService.confirmCertification(certId),
    onSuccess: () => {
      message.success('Certification confirmed');
      qc.invalidateQueries({ queryKey: ['epc-spc', spcId] });
      qc.invalidateQueries({ queryKey: ['epc-subcontract-certs-for-spc', agreementId] });
      qc.invalidateQueries({ queryKey: ['epc-subcontract-workspace', agreementId] });
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  const convertSiM = useMutation({
    mutationFn: (args: {
      certId: string;
      departmentId: string;
      expenseAccountId: string;
      vatConfigurationId?: string | null;
      inputVatAccountId?: string | null;
      applyVat: boolean;
    }) =>
      epcSubcontractAdminService.convertCertificationToSupplierInvoice(args.certId, {
        departmentId: args.departmentId,
        expenseAccountId: args.expenseAccountId,
        vatConfigurationId: args.vatConfigurationId,
        inputVatAccountId: args.inputVatAccountId,
        applyVat: args.applyVat,
      }),
    onSuccess: (data) => {
      message.success('Draft supplier invoice created');
      setConvertSiOpen(false);
      const row = data as Record<string, unknown>;
      const bridge = row.supplierInvoice as Record<string, unknown> | undefined;
      const cert = row.certification as Record<string, unknown> | undefined;
      const siId = String(
        bridge?.supplierInvoiceId ||
          cert?.supplier_invoice_id ||
          row.supplierInvoiceId ||
          row.supplier_invoice_id ||
          row.id ||
          ''
      );
      qc.invalidateQueries({ queryKey: ['epc-subcontract-certs-for-spc', agreementId] });
      qc.invalidateQueries({ queryKey: ['epc-subcontract-workspace', agreementId] });
      if (siId) navigate(`/fin/direct-purchase-invoices/${siId}`);
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      if (!agreementActive && isNew) {
        message.warning('Agreement should be Active/Approved before issuing certificates');
      }
      const hasWork = lines.some((l) => l.appliedAmount > 0);
      const isRetentionOnly = values.certificateKind === 'RetentionRelease';
      if (!hasWork && !isRetentionOnly && !(Number(values.retentionReleasedAmount) > 0)) {
        message.error('Enter this-period amounts on at least one line (or use Retention release)');
        return;
      }
      for (const ln of lines) {
        if (ln.appliedAmount > ln.remaining + 0.01) {
          message.error(`Line ${ln.lineCode || ln.description} exceeds remaining uncertified amount`);
          return;
        }
      }
      saveM.mutate(buildPayload(values));
    } catch {
      /* validation */
    }
  };

  const printSpc = async () => {
    try {
      const cert = isNew
        ? null
        : await epcSubcontractAdminService.getPaymentCertificate(spcId!);
      if (!cert) {
        message.info('Save the certificate before printing');
        return;
      }
      openPmvPrintOverlay((onClose) => (
        <PrintableSubcontractDocument
          kind="certificate"
          certificate={cert}
          agreement={
            agreement
              ? {
                  ...agreement,
                  agreement_number: agreement.agreementNumber ?? agreement.agreement_number,
                  contract_sum: agreement.contractSum ?? agreement.contract_sum,
                  supplier_id: agreement.supplierId ?? agreement.supplier_id,
                  project_id: agreement.projectId ?? agreement.project_id,
                }
              : undefined
          }
          onClose={onClose}
        />
      ));
    } catch (e: unknown) {
      message.error(apiErr(e));
    }
  };

  if (draftQ.isLoading || (!isNew && spcQ.isLoading)) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (draftQ.isError) {
    return (
      <div style={{ padding: 16 }}>
        <Alert type="error" showIcon message={apiErr(draftQ.error)} />
      </div>
    );
  }

  const certStatus = linkedCert ? String(linkedCert.status) : '';
  const siId = linkedCert
    ? String(linkedCert.supplier_invoice_id || linkedCert.supplierInvoiceId || '')
    : '';

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: '0 auto' }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() =>
            navigate(`/epc/commercial/subcontracts/${agreementId}?tab=certificates`)
          }
        >
          Back to workspace
        </Button>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            {isNew
              ? 'New Payment Certificate'
              : `Payment Certificate ${String(spcQ.data?.certificate_number || '')}`}
          </Title>
          <Text type="secondary">
            {String(agreement?.agreementNumber || '')} — {String(agreement?.title || '')}
            {!isNew ? (
              <>
                {' '}
                · <Tag>{status}</Tag>
              </>
            ) : null}
          </Text>
        </div>
      </Space>

      {!agreementActive ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Agreement is not Active yet"
          description="Submit and approve the Subcontractor Agreement before treating certificates as commercial commitments. You can still draft an SPC."
        />
      ) : null}

      <Form form={form} layout="vertical" disabled={!canEdit}>
        <Card title="Certificate header" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="certificateKind"
                label="Certificate kind"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { value: 'Interim', label: 'Interim' },
                    { value: 'RetentionRelease', label: 'Retention release' },
                    { value: 'Final', label: 'Final' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="periodFrom" label="Period from">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="periodTo" label="Period to">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Text type="secondary">
            Currency {currency} · Contract sum {money(agreement?.contractSum, currency)}
            {kindWatch === 'RetentionRelease'
              ? ' · Retention release typically focuses on retention released (line work optional)'
              : ''}
          </Text>
        </Card>

        <Card
          title="Agreement lines — this period valuation"
          style={{ marginBottom: 16 }}
          extra={
            canEdit ? (
              <Text type="secondary">Edit period qty, % of line, or amount</Text>
            ) : null
          }
        >
          <Table
            size="small"
            pagination={false}
            rowKey="key"
            dataSource={lines}
            scroll={{ x: 1100 }}
            columns={[
              {
                title: 'Code',
                dataIndex: 'lineCode',
                width: 90,
                fixed: 'left',
              },
              {
                title: 'Description',
                dataIndex: 'description',
                width: 220,
              },
              {
                title: 'Line amount',
                dataIndex: 'lineAmount',
                width: 110,
                align: 'right' as const,
                render: (v: number) => money(v, currency),
              },
              {
                title: 'Prev. certified',
                dataIndex: 'previousCertified',
                width: 110,
                align: 'right' as const,
                render: (v: number) => money(v, currency),
              },
              {
                title: 'Remaining',
                dataIndex: 'remaining',
                width: 100,
                align: 'right' as const,
                render: (v: number) => money(v, currency),
              },
              {
                title: 'Period qty',
                dataIndex: 'thisPeriodQty',
                width: 100,
                render: (_: unknown, row) =>
                  canEdit ? (
                    <InputNumber
                      min={0}
                      value={row.thisPeriodQty}
                      style={{ width: '100%' }}
                      onChange={(v) => updateLine(row.key, { thisPeriodQty: Number(v) || 0 })}
                    />
                  ) : (
                    String(row.thisPeriodQty || '—')
                  ),
              },
              {
                title: 'Period %',
                dataIndex: 'thisPeriodPct',
                width: 90,
                render: (_: unknown, row) =>
                  canEdit ? (
                    <InputNumber
                      min={0}
                      max={100}
                      value={row.thisPeriodPct}
                      style={{ width: '100%' }}
                      onChange={(v) => updateLine(row.key, { thisPeriodPct: Number(v) || 0 })}
                    />
                  ) : (
                    `${row.thisPeriodPct || 0}%`
                  ),
              },
              {
                title: 'This period amt',
                dataIndex: 'appliedAmount',
                width: 120,
                render: (_: unknown, row) =>
                  canEdit ? (
                    <InputNumber
                      min={0}
                      max={row.remaining || undefined}
                      value={row.appliedAmount}
                      style={{ width: '100%' }}
                      onChange={(v) => updateLine(row.key, { appliedAmount: Number(v) || 0 })}
                    />
                  ) : (
                    money(row.appliedAmount, currency)
                  ),
              },
              {
                title: 'Cumulative',
                key: 'cum',
                width: 110,
                align: 'right' as const,
                render: (_: unknown, row) =>
                  money(round2(row.previousCertified + row.appliedAmount), currency),
              },
            ]}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={7}>
                  <Text strong>Work gross (this period)</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7}>
                  <Text strong>{money(workGross, currency)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} />
              </Table.Summary.Row>
            )}
          />
        </Card>

        <Card title="Valuation extras" style={{ marginBottom: 16 }}>
          <Space style={{ marginBottom: 12 }} wrap>
            <Button
              loading={siteEvidenceMut.isPending}
              onClick={() => siteEvidenceMut.mutate()}
              disabled={!agreementId || !canEdit}
            >
              Suggest from site stock
            </Button>
            <Text type="secondary">
              Advisory only — fills free-issue excluded from FOC / WIP evidence. Does not post inventory.
            </Text>
          </Space>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item name="materialsOnSite" label="Materials on site">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="includeMaterialsOnSite" valuePropName="checked">
                <Checkbox style={{ marginTop: 30 }}>Include MOS in payable gross</Checkbox>
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="dayworksAmount" label="Dayworks">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="attendancePct" label="Attendance %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="freeIssueAmount"
                label="Free-issue (excluded)"
                extra="Tracked only — never payable"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Commercial deductions" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Checkbox
                checked={useAdvanceRate}
                disabled={!canEdit}
                onChange={(e) => setUseAdvanceRate(e.target.checked)}
              >
                Advance recovery by rate %
              </Checkbox>
              <Row gutter={16} style={{ marginTop: 8 }}>
                {useAdvanceRate ? (
                  <Col xs={12} md={8}>
                    <Form.Item name="advanceRecoveryRatePct" label="Advance recovery %">
                      <InputNumber min={0} max={100} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                ) : (
                  <Col xs={12} md={8}>
                    <Form.Item name="advanceRecoveryAmount" label="Advance recovery amount">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                )}
                <Col xs={12} md={8}>
                  <Form.Item
                    name="remainingAdvanceBalance"
                    label="Remaining advance balance"
                    extra={
                      defaults?.advanceRecoveredToDate != null
                        ? `Recovered to date: ${money(defaults.advanceRecoveredToDate, currency)}`
                        : undefined
                    }
                  >
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>
            <div>
              <Checkbox
                checked={useRetentionRate}
                disabled={!canEdit}
                onChange={(e) => setUseRetentionRate(e.target.checked)}
              >
                Retention withhold by rate %
              </Checkbox>
              <Row gutter={16} style={{ marginTop: 8 }}>
                {useRetentionRate ? (
                  <Col xs={12} md={8}>
                    <Form.Item name="retentionRatePct" label="Retention %">
                      <InputNumber min={0} max={100} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                ) : (
                  <Col xs={12} md={8}>
                    <Form.Item name="retentionWithheldAmount" label="Retention withheld">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                )}
                <Col xs={12} md={8}>
                  <Form.Item name="retentionReleasedAmount" label="Retention released">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item name="setOffAmount" label="Set-off">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Space>
        </Card>

        <Card title="Totals preview" style={{ marginBottom: 16 }}>
          <Descriptions bordered size="small" column={{ xs: 1, md: 2 }}>
            <Descriptions.Item label="Work gross">{money(preview.workGross, currency)}</Descriptions.Item>
            <Descriptions.Item label="Dayworks">{money(preview.dayworksAmount, currency)}</Descriptions.Item>
            <Descriptions.Item label="Materials on site">
              {money(preview.materialsOnSite, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Attendance">
              {money(preview.attendanceAmount, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Gross before commercial">
              {money(preview.grossBeforeCommercial, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Free-issue (excluded)">
              {money(preview.freeIssueExcluded, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Advance recovery">
              {money(preview.advanceRecovery, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Retention withheld">
              {money(preview.retentionWithheld, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Retention released">
              {money(preview.retentionReleased, currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Set-off">{money(preview.setOffAmount, currency)}</Descriptions.Item>
            <Descriptions.Item label="Net payable this certificate">
              <Text strong>{money(preview.netApplied, currency)}</Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Form>

      {!isNew ? (
        <Card title="Approval & payment flow" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text type="secondary">
              Flow: Save draft → Submit SPC → Approve → Create certification → Confirm → Convert to
              Draft SI → Procurement posts SI → Finance Payment Voucher.
            </Text>
            <Space wrap>
              {status === 'Draft' || status === 'Rejected' ? (
                <Button type="primary" loading={submitM.isPending} onClick={() => submitM.mutate()}>
                  Submit for approval
                </Button>
              ) : null}
              {(status === 'Approved' || status === 'Submitted') && !linkedCert ? (
                <Button loading={createCertM.isPending} onClick={() => createCertM.mutate()}>
                  Create certification
                </Button>
              ) : null}
              {linkedCert && certStatus !== 'Certified' ? (
                <Button
                  loading={confirmCertM.isPending}
                  onClick={() => confirmCertM.mutate(String(linkedCert.id))}
                >
                  Confirm certification
                </Button>
              ) : null}
              {linkedCert && certStatus === 'Certified' && !siId ? (
                <Button
                  type="primary"
                  loading={convertSiM.isPending}
                  onClick={() => setConvertSiOpen(true)}
                >
                  Convert to Draft SI
                </Button>
              ) : null}
              {siId ? (
                <Link to={`/fin/direct-purchase-invoices/${siId}`}>
                  Open Direct PI
                  {linkedCert?.supplier_invoice_doc_number
                    ? ` (${String(linkedCert.supplier_invoice_doc_number)})`
                    : ''}
                </Link>
              ) : null}
              <Link to="/fin/payment-vouchers">Payment vouchers (Finance)</Link>
            </Space>
            {linkedCert ? (
              <Descriptions size="small" bordered column={1}>
                <Descriptions.Item label="Certification">
                  {String(linkedCert.certification_number || linkedCert.id)} · {certStatus}
                </Descriptions.Item>
                <Descriptions.Item label="Certified net">
                  {money(linkedCert.certified_net_amount, currency)}
                </Descriptions.Item>
                {siId ? (
                  <Descriptions.Item label="Direct purchase invoice">
                    <Link to={`/fin/direct-purchase-invoices/${siId}`}>
                      {String(
                        linkedCert.supplier_invoice_doc_number ||
                          linkedCert.supplier_invoice_number ||
                          'Open'
                      )}
                    </Link>
                    {linkedCert.supplier_invoice_status
                      ? ` · ${String(linkedCert.supplier_invoice_status)}`
                      : ''}
                    {Number(linkedCert.supplier_invoice_gl_posted) === 1
                      ? ' · GL posted (eligible for PV)'
                      : ' · Not posted yet (post in Finance before PV)'}
                  </Descriptions.Item>
                ) : null}
              </Descriptions>
            ) : null}
            <ApprovalPanel
              entityType={ENTITY_TYPE_SUBCONTRACT_PAYMENT_CERTIFICATE}
              entityId={spcId!}
            />
          </Space>
        </Card>
      ) : null}

      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Link to={`/epc/commercial/subcontracts/${agreementId}?tab=certificates`}>Cancel</Link>
        <Space wrap>
          {!isNew ? (
            <Button icon={<PrinterOutlined />} onClick={printSpc}>
              Print
            </Button>
          ) : null}
          {canEdit ? (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saveM.isPending}
              onClick={onSave}
            >
              {isNew ? 'Create certificate' : 'Save changes'}
            </Button>
          ) : null}
        </Space>
      </Space>

      <ConvertCertificationToSiModal
        open={convertSiOpen}
        confirming={convertSiM.isPending}
        defaultDepartmentId={
          agreement?.department_id ? String(agreement.department_id) : null
        }
        onCancel={() => setConvertSiOpen(false)}
        onConfirm={(payload) => {
          if (!linkedCert?.id) return;
          convertSiM.mutate({ certId: String(linkedCert.id), ...payload });
        }}
      />
    </div>
  );
};

export default EpcSubcontractPaymentCertificateFormPage;
